import { useEffect, useState } from 'react';
import { CHART_CONSTANTS } from '../../dre/drill-down/constants';
import { formatCompactCurrency } from '../../dre/drill-down/utils';

export interface ChartTooltipProps {
  /** Whether the tooltip is visible */
  visible: boolean;
  /** X position in pixels */
  x: number;
  /** Y position in pixels */
  y: number;
  /** Period label (e.g., "Jan/24") */
  periodLabel: string;
  /** Current period value */
  currentValue: number;
  /** Comparison period value (optional) */
  comparisonValue?: number;
  /** Comparison period label (e.g., "Jan/23") */
  comparisonPeriodLabel?: string;
  /** Format as currency (default: true) */
  isCurrency?: boolean;
}

/**
 * Chart tooltip component with comparison delta display.
 *
 * Features:
 * - Shows current value formatted as currency
 * - When comparison data provided, shows delta percentage with directional indicator
 * - Semantic colors for positive/negative deltas
 * - Dark theme styling matching brand
 * - Keyboard accessible with role="tooltip"
 * - Respects prefers-reduced-motion for animations
 */
export function ChartTooltip({
  visible,
  x,
  y,
  periodLabel,
  currentValue,
  comparisonValue,
  comparisonPeriodLabel,
  isCurrency = true,
}: ChartTooltipProps): JSX.Element | null {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  if (!visible) {
    return null;
  }

  // Calculate delta percentage when comparison is available
  const hasComparison = comparisonValue !== undefined && comparisonValue > 0;
  const delta = hasComparison
    ? ((currentValue - comparisonValue) / comparisonValue) * 100
    : 0;
  const isPositive = delta >= 0;
  const deltaIndicator = isPositive ? '▲' : '▼';
  const deltaText = `${deltaIndicator} ${isPositive ? '+' : ''}${delta.toFixed(1)}%`;

  // Format values
  const formattedCurrentValue = isCurrency
    ? formatCompactCurrency(currentValue)
    : Math.round(currentValue).toLocaleString('pt-BR');

  const { COLORS } = CHART_CONSTANTS;

  return (
    <div
      role="tooltip"
      tabIndex={0}
      aria-label={`${periodLabel}: ${formattedCurrentValue}${hasComparison ? `, ${deltaText} vs ${comparisonPeriodLabel}` : ''}`}
      className={`chart-tooltip ${prefersReducedMotion ? 'chart-tooltip-instant' : ''}`}
      style={{
        position: 'absolute',
        left: x,
        top: y,
        transform: 'translate(-50%, -100%)',
        pointerEvents: 'none',
      }}
    >
      {/* Period label */}
      <div className="chart-tooltip-period">
        {periodLabel}
      </div>

      {/* Current value */}
      <div className="chart-tooltip-value">
        {formattedCurrentValue}
      </div>

      {/* Delta indicator (only when comparison available) */}
      {hasComparison && (
        <div
          className="chart-tooltip-delta"
          style={{
            color: isPositive ? COLORS.POSITIVE : COLORS.NEGATIVE
          }}
        >
          {deltaText}
          <span className="chart-tooltip-comparison-label">
            {' vs '}{comparisonPeriodLabel}
          </span>
        </div>
      )}
    </div>
  );
}

export default ChartTooltip;
